import { html } from '../utils/development/formatter';
import { getEl } from '../utils/get-el';
import { DraggableModal } from './draggable-modal';
import './ai-chat-modal.css';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export class AIChatModal extends DraggableModal {
  private static readonly id = 'ai-chat-modal';
  private static instance_: AIChatModal | null = null;
  private messages: ChatMessage[] = [];
  private apiKey: string = '';
  private isApiKeySet: boolean = false;

  private constructor() {
    if (AIChatModal.instance_) {
      throw new Error('Use getInstance() instead of new.');
    }

    super(AIChatModal.id, {
      title: 'TK - AI Tech Knowledge Bot',
      width: '600px',
    });

    // Load API key from localStorage
    this.loadApiKey();
  }

  static getInstance(): AIChatModal {
    this.instance_ ??= new AIChatModal();
    return this.instance_;
  }

  protected getModalContentHtml(): string {
    if (!this.isApiKeySet) {
      return html`
        <div class="ai-chat-modal">
          <div class="ai-chat-modal__api-key-section">
            <p class="ai-chat-modal__api-key-label">Enter your OpenAI API Key:</p>
            <input
              type="password"
              id="ai-chat-api-key-input"
              class="ai-chat-modal__api-key-input"
              placeholder="sk-..."
              value="${this.apiKey}"
            />
            <div class="ai-chat-modal__api-key-actions">
              <button id="ai-chat-save-api-key-btn" class="ai-chat-modal__btn ai-chat-modal__btn--primary">
                Save & Continue
              </button>
            </div>
            <p class="ai-chat-modal__api-key-note">
              Your API key is stored locally in your browser and never sent to our servers.
            </p>
          </div>
        </div>
      `;
    }

    return html`
      <div class="ai-chat-modal">
        <div class="ai-chat-modal__messages" id="ai-chat-messages">
          ${this.messages.length === 0
            ? '<div class="ai-chat-modal__welcome">How can I help you with SignalRange today?</div>'
            : this.messages
                .map(
                  (msg) => html`
                    <div class="ai-chat-modal__message ai-chat-modal__message--${msg.role}">
                      <div class="ai-chat-modal__message-content">${this.escapeHtml(msg.content)}</div>
                    </div>
                  `
                )
                .join('')}
        </div>
        <div class="ai-chat-modal__input-section">
          <textarea
            id="ai-chat-input"
            class="ai-chat-modal__input"
            placeholder="Ask me anything about SignalRange..."
            rows="3"
          ></textarea>
          <div class="ai-chat-modal__input-actions">
            <button id="ai-chat-send-btn" class="ai-chat-modal__btn ai-chat-modal__btn--primary">
              Send
            </button>
            <button id="ai-chat-clear-btn" class="ai-chat-modal__btn ai-chat-modal__btn--secondary">
              Clear
            </button>
            <button id="ai-chat-settings-btn" class="ai-chat-modal__btn ai-chat-modal__btn--secondary">
              Settings
            </button>
          </div>
        </div>
      </div>
    `;
  }

  override open(cb?: () => void): void {
    super.open(() => {
      // Refresh content when opening to ensure it's up to date
      // Use setTimeout to ensure the modal DOM is fully created
      setTimeout(() => {
        this.refreshModal();
      }, 0);
      if (cb) {
        cb();
      }
    });
  }

  protected onOpen(): void {
    super.onOpen();
  }

  private initializeEventListeners(): void {
    if (!this.isApiKeySet) {
      const saveBtn = getEl('ai-chat-save-api-key-btn') as HTMLButtonElement;
      const apiKeyInput = getEl('ai-chat-api-key-input') as HTMLInputElement;

      if (saveBtn) {
        saveBtn.addEventListener('click', () => this.handleSaveApiKey());
      }

      if (apiKeyInput) {
        apiKeyInput.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.handleSaveApiKey();
          }
        });
      }
      return;
    }

    const sendBtn = getEl('ai-chat-send-btn') as HTMLButtonElement;
    const clearBtn = getEl('ai-chat-clear-btn') as HTMLButtonElement;
    const settingsBtn = getEl('ai-chat-settings-btn') as HTMLButtonElement;
    const input = getEl('ai-chat-input') as HTMLTextAreaElement;

    if (sendBtn) {
      sendBtn.addEventListener('click', () => this.handleSendMessage());
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.handleClearChat());
    }

    if (settingsBtn) {
      settingsBtn.addEventListener('click', () => this.handleOpenSettings());
    }

    if (input) {
      input.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.handleSendMessage();
        }
      });
    }
  }

  private handleSaveApiKey(): void {
    const apiKeyInput = getEl('ai-chat-api-key-input') as HTMLInputElement;
    if (apiKeyInput) {
      const key = apiKeyInput.value.trim();
      if (key) {
        this.apiKey = key;
        this.isApiKeySet = true;
        localStorage.setItem('openai_api_key', this.apiKey);
        this.refreshModal();
      }
    }
  }

  private handleOpenSettings(): void {
    this.isApiKeySet = false;
    this.refreshModal();
  }

  private handleClearChat(): void {
    this.messages = [];
    this.refreshModal();
  }

  private async handleSendMessage(): Promise<void> {
    const input = getEl('ai-chat-input') as HTMLTextAreaElement;
    if (!input || !input.value.trim()) {
      return;
    }

    const userMessage = input.value.trim();
    input.value = '';

    // Add user message
    this.messages.push({ role: 'user', content: userMessage });
    this.refreshModal();

    // Add loading message
    const loadingId = `loading-${Date.now()}`;
    const messagesContainer = getEl('ai-chat-messages');
    if (messagesContainer) {
      messagesContainer.insertAdjacentHTML(
        'beforeend',
        html`
          <div id="${loadingId}" class="ai-chat-modal__message ai-chat-modal__message--assistant">
            <div class="ai-chat-modal__message-content ai-chat-modal__message-content--loading">
              <span class="ai-chat-modal__loading-dot"></span>
              <span class="ai-chat-modal__loading-dot"></span>
              <span class="ai-chat-modal__loading-dot"></span>
            </div>
          </div>
        `
      );
      this.scrollToBottom();
    }

    try {
      const response = await this.callOpenAI(userMessage);
      const assistantMessage = response.choices[0]?.message?.content || 'Sorry, I could not generate a response.';

      // Remove loading message
      const loadingEl = getEl(loadingId);
      if (loadingEl) {
        loadingEl.remove();
      }

      // Add assistant message
      this.messages.push({ role: 'assistant', content: assistantMessage });
      this.refreshModal();
    } catch (error) {
      // Remove loading message
      const loadingEl = getEl(loadingId);
      if (loadingEl) {
        loadingEl.remove();
      }

      // Add error message
      this.messages.push({
        role: 'assistant',
        content: `Error: ${error instanceof Error ? error.message : 'Failed to get response from OpenAI. Please check your API key and try again.'}`,
      });
      this.refreshModal();
    }
  }

  private async callOpenAI(userMessage: string): Promise<any> {
    if (!this.apiKey) {
      throw new Error('API key not set');
    }

    // Create system message with context about SignalRange
    const systemMessage: ChatMessage = {
      role: 'assistant',
      content: `You are a helpful assistant for SignalRange, a Space Electronic Warfare Training Platform. 
SignalRange is a web-based training simulator for space electronic warfare operations. It includes:
- RF equipment simulation (antennas, BUC, LNB, HPA, filters, couplers, GPSDO, OMT)
- Real-time spectrum analyzers with spectral density plots and waterfall displays
- Satellite tracking and signal processing
- Transmitters and receivers
- Signal gain/loss calculations through RF chains
- Multi-team support with state synchronization

Help users understand how to use the platform, troubleshoot issues, and learn about electronic warfare concepts.`,
    };

    const messages = [
      { role: 'system', content: systemMessage.content },
      ...this.messages.map((msg) => ({ role: msg.role, content: msg.content })),
      { role: 'user', content: userMessage },
    ];

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: messages,
        temperature: 0.7,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  private loadApiKey(): void {
    const storedKey = localStorage.getItem('openai_api_key');
    if (storedKey) {
      this.apiKey = storedKey;
      this.isApiKeySet = true;
    }
  }

  private refreshModal(): void {
    if (!this.boxEl) {
      return;
    }

    const contentEl = this.boxEl.querySelector('.draggable-box__content');
    if (contentEl) {
      contentEl.innerHTML = this.getModalContentHtml();
      // Small delay to ensure DOM is updated before adding listeners
      setTimeout(() => {
        this.initializeEventListeners();
        this.scrollToBottom();
      }, 0);
    }
  }

  private scrollToBottom(): void {
    const messagesContainer = getEl('ai-chat-messages');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  close(): void {
    super.close();
  }
}

